<template>
  <v-container class="py-4 text-start mt-16 pt-6">
    <div v-if="isLoading" class="loader">
      <div class="justify-content-center jimu-primary-loading"></div>
    </div>
    <v-row v-else>
      <!-- Left Section -->
      <v-col cols="12" md="8">
        <!-- Trip Card -->
        <v-card outlined class="pa-4 card" :loading="isLoading">
          <div class="trip-header mb-3 d-flex align-center">
            <v-btn small variant="outlined" color="primary" class="text-capitalize">
              {{ response?.data?.tripTypeDesc }}
            </v-btn>
          </div>
          <br />
          <!-- From / To -->
          <v-row class="align-center mb-3" no-gutters>
            <v-col class="d-flex align-center justify-start">
              <v-icon size="10" color="green" class="mr-2">mdi-circle</v-icon>
              <span class="font-weight-bold">
                {{ truncate(response?.data?.routes?.source?.address) }}
              </span>
            </v-col>

            <!-- Dotted Line + Location Icon -->
            <v-col class="d-flex align-center justify-center">
              <div class="flex-grow-1" style="border-top: 1px dashed #d1d5db; position: relative">
                <v-icon size="16" color="grey" style="
                    position: absolute;
                    top: -8px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: white;
                  ">
                  mdi-map-marker-outline
                </v-icon>
              </div>
            </v-col>

            <!-- Destination -->
            <v-col class="d-flex align-center justify-end">
              <v-icon size="10" color="red" class="mr-2">mdi-circle</v-icon>
              <span class="font-weight-bold">
                {{ truncate(response?.data?.routes?.destination?.address) }}
              </span>
            </v-col>
          </v-row>
          <br />
          <!-- Date & Time -->
          <div class="d-flex align-center text-body-2 text-grey-darken-1 mb-3">
            <v-icon small class="mr-1">mdi-clock-outline</v-icon>
            {{ formatDate(response?.data?.routes?.startDate ?? '') }},
            {{ response?.data?.routes?.startTime }}
          </div>
          <br />

          <!-- Car & Aggregator Info -->
          <v-row class="align-center mb-3 info">
            <v-col cols="auto">
              <div class="cab-image">
                <!-- {{ routerData?.image ?? 'none' }} -->
                <img :src="displayCabImage" alt="Cab" class="cab-img" />
                <!-- <img src="../assets/Hatchback_2x.png" alt="Cab" class="cab-img" /> -->
                <div class="fuel-strip">
                  {{ response?.data?.cabRate?.cab.fuelType ?? '' }}
                </div>
              </div>
            </v-col>
            <v-col>
              <!-- Vehicle Name / Type -->
              <div class="font-weight-medium ml-0" style="text-align: left">
                {{ response?.data?.cabRate?.cab.vehicle_type ?? '' }}
                <v-spacer></v-spacer>
                <span class="text--secondary" style="text-align: left; font-size: 13px">or similar</span>
              </div>

              <!-- Seats + Bags -->
              <div class="text--secondary" style="text-align: left; font-size: 13px">
                {{ response?.data?.cabRate?.cab.seatingCapacity ?? '' }} Seats •
                {{ response?.data?.cabRate?.cab.bagCapacity ?? '' }} Bags
              </div>
            </v-col>

            <v-col cols="auto" class="text-right">
              <div class="d-flex align-center">
                <v-icon color="amber" small class="mr-1">mdi-star</v-icon>
                <span class="font-weight-medium">{{ displayRating }}</span>
              </div>

              <button style="
                  background-color: oklch(0.627 0.194 149.214);
                  color: white;
                  width: 78px;
                  border-radius: 8px;
                ">
                Excellent
              </button>
            </v-col>
          </v-row>

          <!-- Verified Partner -->
          <v-row class="align-center partner-info mb-4">
            <v-col cols="auto">
              <v-icon>mdi-shield-outline</v-icon>
            </v-col>
            <v-col class="d-flex align-center">
              <div class="font-weight-bold" style="font-size: 13px; text-align: left">
                Our Verified Partner
              </div>
              <v-spacer></v-spacer>
              <div>
                <v-img :src="mediaUrl + response?.data?.aggregator_logo" alt="Aggregator" width="100"
                  height="40"></v-img>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <!-- What's Included -->
        <v-card outlined class="pa-4 mt-4 border-sm" style="border-radius: 14px" :loading="isLoading">
          <v-row>
            <v-col cols="12" md="6">
              <h3 style="text-align: left">What's Included</h3>
              <div v-for="(item, index) in includedItems" :key="index" class="d-flex align-center mb-2 mt-2">
                <v-icon v-if="!item.toLowerCase().includes('not included')" color="green" size="20"
                  class="mr-2">mdi-check</v-icon>
                <v-icon color="error" size="20" class="mr-2" v-else>mdi-cancel</v-icon>
                <span class="text-body-2">{{ item }}</span>
              </div>
            </v-col>
            <!-- <v-col cols="12" md="6" class="text-right">
              <a href="#" class="text-primary">View Policy</a>
            </v-col> -->
          </v-row>
        </v-card>

        <!-- Cancellation Policy -->
        <v-card outlined class="pa-4 mt-4 border-sm" style="border-radius: 14px" :loading="isLoading">
          <div class="d-flex justify-space-between align-center mb-3">
            <h3 class="text-h6 font-weight-medium">Cancellation Policy</h3>
            <!-- <a href="#" class="text-primary">View Details</a> -->
          </div>
          <v-sheet class="pa-3 d-flex flex-column" style="
              border-radius: 8px;
              background-color: oklch(0.982 0.018 155.826);
            ">
            <div class="d-flex align-center mb-1">
              <v-icon size="20" color="green darken-2" class="mr-2">
                mdi-check
              </v-icon>
              <span class="font-weight-medium text-body-1" style="color: #006400">
                Free cancellation until 2 hours before pickup
              </span>
            </div>
            <span class="text-body-2" style="color: #006400; text-align: left">
              2 hours before pickup time
            </span>
          </v-sheet>
        </v-card>
      </v-col>

      <!-- Right Section -->
      <v-col cols="12" md="4">
        <!-- Alert -->
        <v-alert dense class="mb-4" style="
            border-radius: 14px;
            background-color: oklch(0.98 0.016 73.684);
            border: 1px solid oklch(0.901 0.076 70.697);
          ">
          <h3 style="color: oklch(0.553 0.195 38.402)">
            <v-icon>mdi-information</v-icon> Free cancellation till 2 hr of
            pickup
          </h3>
        </v-alert>

        <!-- Offers -->
        <v-card class="border-sm pa-4" style="border-radius: 14px" :loading="isLoading">
          <h3 class="text-base font-semibold mb-3" style="text-align: left">
            Available Offers
          </h3>

          <!-- User Type Radio Buttons with Taglines -->
          <!-- <v-radio-group  v-model="selectedUserType" column class="mt-4">
            <v-radio value="premium" color="green">
              <template v-slot:label>
                <div>
                  <div class="font-bold d-flex align-center">
                    <span style="font-size: 16px; margin-right: 6px">💳</span>
                    PREMIUM
                  </div>
                  <div class="text-gray-600 text-sm">
                    Get flat ₹150 off on your ride booking!
                  </div>
                </div>
              </template>
</v-radio>
<v-spacer></v-spacer>
<v-radio value="new_user" color="blue">
  <template v-slot:label>
                <div>
                  <div class="font-bold d-flex align-center">
                    <span style="font-size: 16px; margin-right: 6px">🎯</span>
                    NEWUSER
                  </div>
                  <div class="text-gray-600 text-sm">
                    Get flat ₹100 off on your ride booking!
                  </div>
                </div>
              </template>
</v-radio>
</v-radio-group> -->
          <p class="text-center my-12">No offers available</p>
          <!-- Coupon Input -->
          <v-row class="align-center mt-3" no-gutters>
            <v-col>
              <v-text-field placeholder="ENTER A COUPON" variant="outlined" density="compact" hide-details
                class="coupon-field" v-model="couponCode"></v-text-field>
            </v-col>
            <v-col cols="auto" class="pl-2">
              <v-btn color="primary" elevation="0" class="rounded-lg" style="min-height: 38px; padding: 0 18px"
                @click="applyCoupon">
                APPLY
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <v-card outlined class="pa-4 mt-4 border-sm" style="border-radius: 14px" :loading="isLoading">
          <h3 class="mb-2" style="text-align: left">Payment Options</h3>

          <!-- Cash Payment Option -->
          <v-row v-if="paymentMethod === 'Cash'" class="align-center mb-2" no-gutters>
            <v-col cols="auto">
              <v-radio-group v-model="paymentOption" class="ma-0 pa-0">
                <v-radio value="cash" class="payment-radio ma-0 pa-0">
                  <template v-slot:label>
                    <div class="d-flex flex-column">
                      <strong style="text-align: left">Pay to Driver</strong>
                      <div class="text-caption text-grey">
                        Pay full amount to driver
                      </div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-col>
            <v-col cols="auto" class="ml-auto" style="margin-top: -8px">
              <span class="font-weight-bold">
                ₹{{ response?.data?.cabRate?.fare.totalAmount }}
              </span>
            </v-col>
          </v-row>

          <!-- Partial Payment -->
          <v-row
            v-else-if="parseFloat(response?.data?.cabRate?.fare.minimumPayable) < parseFloat(response?.data?.cabRate?.fare.totalAmount)"
            class="align-center mb-2" no-gutters>
            <v-col cols="auto">
              <v-radio-group v-model="paymentOption" class="ma-0 pa-0">
                <v-radio value="partial" class="payment-radio ma-0 pa-0">
                  <template v-slot:label>
                    <div class="d-flex flex-column">
                      <strong style="text-align: left">Advance Payment</strong>
                      <div class="text-caption text-grey">
                        Pay rest to the driver
                      </div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-col>
            <v-col cols="auto" class="ml-auto" style="margin-top: -8px">
              <span class="font-weight-bold">
                ₹{{ response?.data?.cabRate?.fare.minimumPayable }}
              </span>
            </v-col>
          </v-row>

          <!-- Full Payment -->
          <v-row v-else class="align-center" no-gutters>
            <v-col cols="auto">
              <v-radio-group v-model="paymentOption" class="ma-0 pa-0">
                <v-radio value="full" class="payment-radio ma-0 pa-0">
                  <template v-slot:label>
                    <div class="d-flex flex-column">
                      <strong>Full Payment</strong>
                      <div class="text-caption text-grey" style="text-align: left">
                        full amount of payment required by partner
                      </div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-col>
            <v-col cols="auto" class="ml-auto" style="margin-top: -8px">
              <span class="font-weight-bold">
                ₹{{ response?.data?.cabRate?.fare.totalAmount }}
              </span>
            </v-col>
          </v-row>

          <v-btn block large color="primary" class="white--text mt-3" elevation="0" @click="initiatePayment">
            {{ paymentMethod === 'Cash' ? 'CONFIRM YOUR BOOKING' : 'PAY NOW' }}
          </v-btn>

          <!-- Payment Confirmation Dialog -->
          <v-dialog v-model="paymentDialog" persistent max-width="600">
            <v-card>
              <v-card-title class="text-h6 d-flex align-center">
                <v-icon v-if="paymentStatus === 'confirming'" color="primary" class="mr-2">mdi-cash-multiple</v-icon>
                <v-icon v-if="paymentStatus === 'processing'" color="info" class="mr-2">mdi-bank-transfer-in</v-icon>
                <v-icon v-if="paymentStatus === 'success'" color="success" class="mr-2">mdi-check-circle</v-icon>
                <v-icon v-if="paymentStatus === 'failed'" color="error" class="mr-2">mdi-alert-circle</v-icon>
                {{ paymentDialogTitle }}
              </v-card-title>

              <v-card-text class="py-6">
                <div v-if="paymentStatus === 'confirming'" class="text-center py-6">
                  <v-icon size="64" color="primary" class="mb-4">mdi-cash-multiple</v-icon>
                  <p class="text-body-1">You will be redirected to a secure payment gateway to complete your
                    transaction.</p>
                  <p class="text-subtitle-2 text--secondary">Please review your payment details before proceeding.</p>
                </div>

                <div v-if="paymentStatus === 'processing'" class="text-center py-6">
                  <v-progress-circular :size="80" :width="6" color="primary" indeterminate
                    class="mb-4"></v-progress-circular>
                  <p class="text-body-1 font-weight-medium">Processing your payment...</p>
                  <p class="text-subtitle-2 text--secondary">Please complete the payment in the opened window.</p>
                  <div class="mt-4">
                    <v-chip color="info" outlined>
                      <v-icon left>mdi-timer</v-icon>
                      Checking payment status...
                    </v-chip>
                  </div>
                </div>

                <div v-if="paymentStatus === 'success'" class="text-center py-6">
                  <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
                  <p class="text-h6 font-weight-bold text-success">Payment Successful!</p>
                  <p class="text-body-1">Your payment has been processed successfully.</p>
                  <v-btn color="success" class="mt-4" @click="closePaymentDialog">
                    Continue
                  </v-btn>
                </div>
                <div v-if="paymentStatus === 'confirmed'" class="text-center py-6">
                  <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
                  <p class="text-h6 font-weight-bold text-success">Booking Confirmed!</p>
                  <p class="text-body-1">Your booking has been confirmed driver will be assign shortly.</p>
                  <v-btn color="success" class="mt-4" @click="$router.push('/AppTrip')">
                    Continue
                  </v-btn>
                </div>

                <div v-if="paymentStatus === 'failed'" class="text-center py-6">
                  <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
                  <p class="text-h6 font-weight-bold text-error">Payment Failed</p>
                  <p class="text-body-1">There was an issue processing your payment.</p>
                  <v-btn color="primary" class="mt-4 mr-2" @click="retryPayment">
                    Try Again
                  </v-btn>
                  <v-btn outlined color="grey" class="mt-4" @click="closePaymentDialog">
                    Cancel
                  </v-btn>
                </div>
              </v-card-text>

              <v-card-actions v-if="paymentStatus === 'confirming'">
                <v-spacer></v-spacer>
                <v-btn color="grey" variant="text" @click="closePaymentDialog">
                  Cancel
                </v-btn>
                <v-btn color="primary" @click="confirmPayment">
                  Confirm & Pay
                </v-btn>
              </v-card-actions>

              <v-card-actions v-if="paymentStatus === 'processing'">
                <v-spacer></v-spacer>
                <v-btn color="grey" variant="text" @click="closePaymentDialog">
                  Cancel Payment
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <div class="text-center mt-3">
            <a href="#" @click.prevent="showFareBreakup = !showFareBreakup" class="text-primary"
              style="text-align: left !important">
              View Fare Break up
            </a>
          </div>

          <div v-if="showFareBreakup" class="mt-3">
            <div class="d-flex justify-space-between">
              <span>Base Fare</span>
              <span>₹{{ response?.data?.cabRate?.fare.baseFare }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <span>Driver Allowance</span>
              <span>
                ₹{{
                  response?.data?.cabRate?.fare.driverAllowance
                }}
              </span>
            </div>
            <div class="d-flex justify-space-between">
              <span>Toll Tax</span>
              <span>₹{{ response?.data?.cabRate?.fare.tollTax }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <span>Night Charges</span>
              <span>₹{{ response?.data?.cabRate?.fare.NightCharges }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <span>Parking Charges</span>
              <span>₹{{ response?.data?.cabRate?.fare.ParkingCharges }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <span>GST</span>
              <span>₹{{ response?.data?.cabRate?.fare.gst }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <span>Extra Fare Per Km</span>
              <span>₹{{ response?.data?.cabRate?.fare.extraPerKmCharge }}</span>
            </div>
            <div class="d-flex justify-space-between font-weight-bold mt-2">
              <span>Total</span>
              <span>₹{{ response?.data?.cabRate?.fare.totalAmount }}</span>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-dialog v-model="ConfirmDialog" max-width="500px" transition="dialog-bottom-transition">
        <v-card class="pa-4" elevation="8" shaped>
          <v-card-title class="text-h6 d-flex align-center">
            <v-icon color="orange darken-2" class="mr-2">mdi-alert-circle-outline</v-icon>
            Confirm Ride
          </v-card-title>

          <v-card-text class="text-body-1">
            <p>By confirming your ride:</p>
            <ul class="pl-4">
              <li>The ride will be booked immediately.</li>
              <li>The amount must be paid directly to the driver.</li>
              <li>Please ensure you are ready at the pickup location.</li>
            </ul>
          </v-card-text>

          <v-card-actions class="justify-end">
            <v-btn text color="grey darken-1" @click="ConfirmDialog = false">
              Cancel
            </v-btn>
            <v-btn color="orange darken-2" dark @click="confirmBooking" :loading="confirmLoading">
              Confirm
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import { parseUrlPayload, safeText, safeImageUrl } from "@/utils/urlPayload";
export default {
  name: "TripCard",
  data() {
    return {
      response: {
        data: {}
      },   // will be replaced after API call
      isLoading: true,
      ConfirmDialog: false,
      confirmLoading: false,
      routerData: null,
      pickupLocation: "",
      gender: "",
      showFareBreakup: false,
      usePickupAsBilling: false,
      paymentOption: "partial",
      offers: [],
      selectedUserType: null,
      couponCode: "",
      transactions: {},
      booking: {
        name: "",
        email: "",
        phone: "",
        before_booking_id: null,
        user_id: null,
        total_fare: null,
        amount_to_be_collected: null,
        aggregator_name: null,
      },
      paymentMethod: null, // To store the payment method from API
      // Payment dialog properties
      paymentDialog: false,
      paymentStatus: 'confirming', // 'confirming', 'processing', 'success', 'failed'
      paymentStatusTimer: null,
      paymentRedirectUrl: '',
    };
  },
  computed: {
    // The only two things this page renders out of the URL payload; everything
    // else on it comes from the booking API. `image` is the important one: an
    // unrestricted :src is the strongest deceptive-content vector here, because a
    // remote or data: image can render a complete fake notice that Vue's text
    // escaping does nothing about.
    displayCabImage() {
      return safeImageUrl(this.routerData?.image, "../assets/transport.png");
    },
    displayRating() {
      return safeText(this.routerData?.rating, 4);
    },
    includedItems() {
      if (!this.response?.data) return []; // nothing until data arrives

      const d = this.response.data;
      console.log('aggregator_name', d)
      const farePerKm = d.cabRate?.fare?.extraPerKmCharge;

      return [
        `Fuel Charges, Parking & State Tax ${(d.aggregator_id == 2 ||
          d.aggregator_id == 15 ||
          d.isTollIncluded)
          ? "included"
          : "not included"
        }`,
        "Pick-up and Drop service included",
        `${d.totalDistance} Km included • ₹${this.rounded(farePerKm)}/Km beyond limit`,
        "Free cancellation up to 2 hours before pickup",
      ];
    },

    paymentDialogTitle() {
      if (this.paymentStatus === 'confirming') return 'Confirm Payment';
      if (this.paymentStatus === 'processing') return 'Processing Payment';
      if (this.paymentStatus === 'success') return 'Payment Successful';
      if (this.paymentStatus === 'failed') return 'Payment Failed';
      if (this.paymentStatus === 'confirmed') return 'Booking Confirmed!';
      return 'Payment';
    }
  },
  mounted() {
    // Caller-supplied payload — a malformed or absent `data` used to throw here
    // and leave a broken page serving HTTP 200. Send those visitors home rather
    // than rendering anything from an untrusted URL. See src/utils/urlPayload.js.
    const payload = parseUrlPayload(this.$route.query.data);
    if (!payload) {
      this.$router.replace({ name: "Home" });
      return;
    }
    this.routerData = payload;

    // Check if we have booking data in sessionStorage
    const bookingSessionKey = `booking_data_${this.routerData.referenceId}`;
    const storedBookingData = sessionStorage.getItem(bookingSessionKey);

    if (storedBookingData) {
      // Use the stored booking data
      this.response.data = JSON.parse(storedBookingData);
      this.paymentMethod = this.response?.data?.payment_method; // Extract payment method from API response
      if (this.paymentMethod === 'Cash') {
        this.paymentOption = 'cash'; // Set payment option to cash if payment method is Cash
      } else if (parseFloat(this.response?.data?.cabRate?.fare.minimumPayable) == parseFloat(this.response?.data?.cabRate?.fare.totalAmount)) {
        this.paymentOption = "full";
      }
      this.paymentRedirectUrl = this.response.data.payUrl;
      this.booking.before_booking_id = this.response.data.before_booking_id;
      this.booking.user_id = this.routerData.user_id;
      this.booking.total_fare = this.response.data.cabRate.fare.totalAmount;
      this.booking.amount_to_be_collected = this.response.data.cabRate.fare.minimumPayable;
      this.booking.aggregator_name = this.response.data.aggregator_id;
      this.isLoading = false;
    } else {
      // No stored data, make the API call
      this.holdCab();
    }

    // Check if there's a pending transaction (user returned from payment gateway)
    const transactionKey = `transactionId_${this.routerData.referenceId}`;
    const transactionId = localStorage.getItem(transactionKey);
    if (transactionId) {
      // User might have come back from payment gateway, check status
      this.paymentDialog = true;
      this.paymentStatus = 'processing';
      this.startPaymentStatusCheck();
    }
  },
  methods: {
    rounded(value) {
      if (!value) return 0;
      return Number(value).toFixed(2); // or toFixed if you want decimals
    },
    truncate(text, length = 25) {
      if (!text) return '';
      return text.length > length ? text.slice(0, length) + '...' : text;
    },
    async holdCab() {
      // routerData was already validated in mounted(). Re-reading the URL here
      // would reintroduce the unchecked path, and threw outright when `data` was
      // absent.
      const payload = { ...this.routerData };
      try {
        let response = await axios.post(
          "/new_book_cabs",
          payload
        );
        this.response.data = response?.data?.data
        this.paymentMethod = this.response?.data?.payment_method; // Extract payment method from API response

        // Store the booking data in sessionStorage to avoid re-calling the API
        const bookingSessionKey = `booking_data_${payload.referenceId}`;
        sessionStorage.setItem(bookingSessionKey, JSON.stringify(this.response.data));

        if (this.paymentMethod === 'Cash') {
          this.paymentOption = 'cash'; // Set payment option to cash if payment method is Cash
        } else if (parseFloat(this.response?.data?.cabRate?.fare.minimumPayable) == parseFloat(this.response?.data?.cabRate?.fare.totalAmount)) {
          this.paymentOption = "full"
        }
        this.booking.before_booking_id = this.response.data.before_booking_id;
        this.paymentRedirectUrl = this.response.data?.payUrl;
        this.booking.user_id = payload.user_id;
        this.booking.total_fare = this.response.data.cabRate.fare.totalAmount;
        this.booking.amount_to_be_collected = this.response.data.cabRate.fare.minimumPayable;
        this.booking.aggregator_name = this.response.data.aggregator_id;
        this.isLoading = false;
      } catch (err) {
        console.error("API Error:", err);
        this.isLoading = false;
      }

    },


    formatDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
      });
    },

    // Payment methods
    async initiatePayment() {
      if (this.paymentRedirectUrl) {
        window.open(this.paymentRedirectUrl, '_blank');
        return;
      }
      if (this.paymentMethod === 'Cash') {
        // For cash payments, directly confirm the booking
        this.ConfirmDialog = true;
      } else {
        // For online payments, show payment dialog
        this.paymentDialog = true;
        this.paymentStatus = 'confirming';
      }
    },

    async confirmPayment() {
      let amount;
      if (this.paymentOption == "partial") {
        amount = this.response?.data?.cabRate?.fare.minimumPayable;
      } else {
        amount = this.response?.data?.cabRate?.fare.totalAmount;
      }

      try {
        // Call the payment API to get the redirect URL
        this.paymentStatus = 'processing';
        const response = await axios.post("/chooseapp/payment", {
          with_gst_included: amount,
          redirect_url: window.location.href
        });

        // Store the redirect URL and transaction ID with booking reference as key
        this.paymentRedirectUrl = response.data.data.data.instrumentResponse.redirectInfo.url;
        const transactionId = response.data.data.data.merchantTransactionId;
        const transactionKey = `transactionId_${this.routerData.referenceId}`;
        localStorage.setItem(transactionKey, transactionId);

        // Start checking payment status with a timeout to prevent infinite checking
        this.startPaymentStatusCheck();

        // Redirect to the payment gateway in the same tab
        window.location.href = this.paymentRedirectUrl;
      } catch (error) {
        console.error("Payment API Error:", error);
        this.paymentStatus = 'failed';
      }
    },

    startPaymentStatusCheck() {
      // Clear any existing timer
      if (this.paymentStatusTimer) {
        clearInterval(this.paymentStatusTimer);
      }

      // Check payment status every 1.5 seconds
      this.paymentStatusTimer = setInterval(async () => {
        try {
          await this.PaymentStatus();
        } catch (error) {
          console.error("Payment status check error:", error);
          // Optionally handle error, maybe show failed status after multiple failed attempts
        }
      }, 5000);
    },

    async PaymentStatus() {
      const transactionKey = `transactionId_${this.routerData.referenceId}`;
      const transactionId = localStorage.getItem(transactionKey);
      if (!transactionId) {
        console.error("No transaction ID found");
        this.paymentStatus = 'failed';
        this.clearPaymentStatusTimer();
        return;
      }

      let payload = {
        transactionId: transactionId,
      };

      try {
        let response = await axios.post("/chooseapp/payment/status", payload);
        console.log("Payment status response:", response.data.data);

        const originalResponse = response.data.data;
        if (originalResponse.code == "PAYMENT_SUCCESS") {
          this.transactions.transaction_id = originalResponse.data.transactionId;
          this.transactions.merchant_transaction_id = originalResponse.data.merchantTransactionId;
          this.transactions.merchant_id = originalResponse.data.merchantId;
          this.transactions.utr = originalResponse.data?.paymentInstrument?.utr;

          // Call confirmBooking when payment is successful
          await this.confirmBooking();

          // Stop the timer to prevent multiple confirmations
          this.clearPaymentStatusTimer();
          localStorage.removeItem(transactionKey);

          // Payment is successful, so set status to confirmed or success based on booking status
          if (this.paymentStatus !== 'confirmed') {
            this.paymentStatus = 'success';
          }
        }
      } catch (error) {
        console.error("Payment status API error:", error);
        this.paymentStatus = 'failed';
        this.clearPaymentStatusTimer();
      }
    },

    async confirmBooking() {
      this.confirmLoading = true;
      // Prepare the payload for the confirm booking API
      let advanceAmount = 0;
      let prePayment = 0;
      let amountToCollect = 0;

      // For cash payments, no advance is collected
      // For online payments, advance is the amount collected
      if (this.paymentMethod !== 'Cash') {
        if (this.paymentOption == "partial") {
          advanceAmount = this.response?.data?.cabRate?.fare.minimumPayable;
        } else {
          advanceAmount = this.response?.data?.cabRate?.fare.totalAmount;
        }
        prePayment = advanceAmount;
      }
      if (this.routerData.aggregator_id == 2) {
        amountToCollect = this.response?.data?.cabRate?.fare.totalAmount;
      }

      const payload = {
        aggregator_id: this.routerData.aggregator_id,
        name: this.routerData.firstName,
        email: this.routerData.email,
        phone: this.routerData.primaryContactNumber,
        before_booking_id: this.booking.before_booking_id,
        user_id: this.booking.user_id,
        total_fare: this.booking.total_fare,
        amount_to_be_collected: amountToCollect,
        saving_amount: 0,
        transaction_id: this.transactions.transaction_id,
        merchant_transaction_id: this.transactions.merchant_transaction_id,
        merchant_id: this.transactions.merchant_id,
        utr: this.transactions.utr,
        payment_type: this.paymentMethod,
        aggregator_name: this.routerData.aggregator_name,
        prePayment: prePayment
      };

      try {
        // Call the confirm booking API
        const response = await axios.post("/confirm_bookings", payload);
        console.log("Booking confirmed:", response.data);
        this.confirmLoading = false;
        if (this.routerData.aggregator_id == 2) {
          this.paymentDialog = true;
        }
        this.paymentStatus = 'confirmed';
      } catch (error) {
        console.error("Booking confirmation failed:", error);
        // Handle error - show error message to user
        alert("Failed to confirm booking. Please try again.");
        this.confirmLoading = false;
      }
    },

    clearPaymentStatusTimer() {
      if (this.paymentStatusTimer) {
        clearInterval(this.paymentStatusTimer);
        this.paymentStatusTimer = null;
      }
    },

    // Clean up transaction data when component is destroyed
    beforeUnmount() {
      this.clearPaymentStatusTimer();
      const transactionKey = `transactionId_${this.routerData.referenceId}`;
      localStorage.removeItem(transactionKey);
    },

    closePaymentDialog() {
      this.paymentDialog = false;
      this.paymentStatus = 'confirming'; // Reset to confirming state when dialog is closed
      this.clearPaymentStatusTimer();
      const transactionKey = `transactionId_${this.routerData.referenceId}`;
      localStorage.removeItem(transactionKey);
    },

    retryPayment() {
      this.paymentStatus = 'confirming';
    }
  },
};
</script>

<style scoped>
.text--secondary {
  color: #6c757d;
}

.info {
  background-color: #f5f5f5;
  margin: 0 0px;
  border-radius: 10px;
}

.car-info {
  background-color: oklch(0.932 0.032 255.585);
}

.card {
  border-radius: 15px;
  box-shadow: none !important;
}

.partner-info {
  background-color: oklch(0.97 0.014 254.604);
  margin: 0 7px;
  border-radius: 10px;
}

.proceed-btn {
  background-color: var(--color-blue-700) !important;
}

.payment-radio {
  margin-bottom: 8px;
}

::v-deep .v-field,
::v-deep .v-text-field,
::v-deep .v-input {
  min-height: 36px !important;
  --v-field-height: 36px !important;
}

::v-deep .v-field__control,
::v-deep .v-field__input {
  min-height: 36px !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

::v-deep .v-field__input>input {
  height: 24px !important;
  font-size: 14px;
}

.travel-details-card {
  margin-top: 8px !important;
}

.travel-title {
  margin-top: 0 !important;
  margin-bottom: 12px;
  font-weight: 600;
}

.section-title {
  margin: 8px 0 4px !important;
  font-size: 14px;
  font-weight: 500;
}

.cab-image {
  height: 80px;
  background: none;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
}

.cab-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.fuel-strip {
  width: 100%;
  text-align: center;
  font-size: 12px;
  font-weight: bold;
  padding: 2px 0;
  background-color: #009688;
}

.fuel-CNG {
  background-color: #009688;
}

.fuel-Diesel-Petrol {
  background-color: #007bff;
}

.fuel-Electric {
  background-color: #4caf50;
}

.v-card,
.v-alert {
  box-shadow: none !important;
}
</style>
